<template>
  <div class="settings-page integration">
    <div class="title">
      <div>
        <img :src="config.icon" class="header-icon" />
        <h2>{{ config.title }}</h2>
      </div>
      <div class="actions">
        <BaseLink @click="openUrl(config.helpUrl)">
          <template #icon><ExternalIcon /></template>Help
        </BaseLink>
      </div>
    </div>

    <p class="description">{{ config.description }}</p>

    <!-- Accounts with the scope granted — toggle on/off -->
    <section v-if="scopedProfiles.length > 0">
      <div class="section-header">
        <h3>Connected</h3>
        <BaseButton
          v-if="feature === 'calendar'"
          kind="main"
          type="secondary"
          :state="refreshingStatus.state"
          :text="refreshingStatus.title"
          @click="refreshCalendars"
        >
          <template #icon><RefreshIcon /></template>
        </BaseButton>
      </div>
      <div
        v-for="profile in scopedProfiles"
        :key="profile.kind.identity"
        class="entry"
        :class="{ disconnected: !isFeatureActive(profile) }"
      >
        <div class="account">
          <img src="../../assets/apps/google.svg" class="account-icon" />
          <label>{{ profile.email }}</label>
        </div>
        <div class="entry-actions">
          <BaseButton
            v-if="isFeatureActive(profile)"
            kind="auxiliary"
            tint="red"
            text="Disconnect"
            :state="accountActionState(profile)"
            @click="disconnectAccount(profile)"
          />
          <BaseButton
            v-else
            kind="main"
            type="secondary"
            text="Connect"
            :state="accountActionState(profile)"
            @click="connectAccount(profile)"
          />
          <span
            class="removal-hint"
            v-tippy="{ content: removalHint, arrow: false, placement: 'top' }"
          >
            <InfoIcon />
          </span>
        </div>
      </div>
    </section>

    <!-- Accounts without the scope — Enable button to request it -->
    <section v-if="unscopedProfiles.length > 0">
      <h3>Available accounts</h3>
      <div
        v-for="profile in unscopedProfiles"
        :key="profile.kind.identity"
        class="entry"
      >
        <div class="account">
          <img src="../../assets/apps/google.svg" class="account-icon" />
          <label>{{ profile.email }}</label>
        </div>
        <BaseButton
          kind="main"
          type="primary"
          text="Enable"
          :state="
            isUpdating || updatingAccount !== null ? 'disabled' : 'default'
          "
          @click="enableFeature(profile)"
        />
      </div>
    </section>

    <!-- Add account (always visible) -->
    <section class="add-account">
      <BaseButton
        kind="main"
        type="secondary"
        text="Add account"
        @click="addAccount"
      >
        <template #icon><AddIcon /></template>
      </BaseButton>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";

import type {
  GoogleScope,
  Profile,
  ProfileFeature,
  ProfileGoogle,
} from "@ctrl/controller-rpc";

import gcalIcon from "@/assets/apps/gcal.svg";
import contactsIcon from "@/assets/contacts.svg";
import BaseButton, { ButtonState } from "@/components/base/BaseButton.vue";
import BaseLink from "@/components/base/BaseLink.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import ExternalIcon from "@/components/icons/ExternalIcon.vue";
import InfoIcon from "@/components/icons/InfoIcon.vue";
import RefreshIcon from "@/components/icons/RefreshIcon.vue";
import * as inject from "@/components/inject";
import { useReleaser } from "@/ref";
import { openExternalUrl } from "@/utils/open-external-url";

import { notifyCheckBrowser } from "../utils";

type GoogleProfile = Profile & { kind: ProfileGoogle & { provider: "google" } };

const removalHint =
  "To remove the integration completely, remove Routine from your " +
  "Google account's third-party apps & services.";

interface FeatureConfig {
  title: string;
  description: string;
  helpUrl: string;
  icon: string;
  stateField: keyof Pick<ProfileGoogle, "calendars_state" | "contacts_state">;
  scopeField: keyof GoogleScope;
}

const configs: Record<ProfileFeature, FeatureConfig> = {
  calendar: {
    title: "Google Calendar",
    description:
      "Sync your Google Calendar to see and manage events in Agenda and Calendar.",
    helpUrl: "https://routine.co/integrations/services/google-calendar",
    icon: gcalIcon,
    stateField: "calendars_state",
    scopeField: "calendar",
  },
  contacts: {
    title: "Google Contacts",
    description: "Sync your Google Contacts to search and use them in Routine.",
    helpUrl: "https://routine.co/integrations/services/google-contacts",
    icon: contactsIcon,
    stateField: "contacts_state",
    scopeField: "contacts",
  },
};

export default defineComponent({
  name: "IntegrationsGoogleFeature",
  components: {
    AddIcon,
    BaseButton,
    BaseLink,
    ExternalIcon,
    InfoIcon,
    RefreshIcon,
  },
  props: {
    orgId: { type: String, required: true },
    feature: {
      type: String as PropType<ProfileFeature>,
      required: true,
    },
  },
  async setup(props) {
    const controller = inject.controller();

    const config = configs[props.feature];

    const releaser = useReleaser();
    const identities = await releaser.add(controller.auth.profiles());

    const googleProfiles = computed<GoogleProfile[]>(() =>
      identities.value.filter(
        (p): p is GoogleProfile => p.kind.provider === "google"
      )
    );

    const hasScope = (profile: GoogleProfile): boolean =>
      !!profile.kind.granted_scopes[config.scopeField];

    const isFeatureActive = (profile: GoogleProfile): boolean =>
      profile.kind[config.stateField] != null;

    const scopedProfiles = computed(() =>
      googleProfiles.value.filter(hasScope)
    );

    const unscopedProfiles = computed(() =>
      googleProfiles.value.filter((p) => !hasScope(p))
    );

    const isUpdating = ref(false);

    async function enableFeature(profile: GoogleProfile) {
      isUpdating.value = true;
      try {
        notifyCheckBrowser(controller);
        await controller.auth.profileSetFeature(
          profile.kind.identity,
          props.feature,
          true
        );
        const desiredScopes = {
          ...profile.kind.granted_scopes,
          [config.scopeField]: true,
        };
        const info = await controller.auth.requestScopes(
          profile.kind.identity,
          desiredScopes,
          window.location.href
        );
        if (info) {
          openExternalUrl(info.grant_uri, null, false);
          const onVisible = async () => {
            if (document.visibilityState === "visible") {
              document.removeEventListener("visibilitychange", onVisible);
              await controller.auth.refresh();
            }
          };
          document.addEventListener("visibilitychange", onVisible);
        } else {
          await controller.auth.refresh();
        }
      } finally {
        isUpdating.value = false;
      }
    }

    // Identity of the account whose connection is currently being toggled.
    const updatingAccount = ref<string | null>(null);

    // Turn the feature on or off for this account. The account already has
    // the scope granted on the Google side, so no consent flow is needed
    // either way; disconnecting leaves the OAuth grant untouched. Once no
    // account is connected, the integration goes back to "Available" in the
    // catalog.
    async function setAccountConnected(
      profile: GoogleProfile,
      connected: boolean
    ) {
      updatingAccount.value = profile.kind.identity;
      try {
        await controller.auth.profileSetFeature(
          profile.kind.identity,
          props.feature,
          connected
        );
        await controller.auth.refresh();
      } finally {
        updatingAccount.value = null;
      }
    }

    const connectAccount = (profile: GoogleProfile) =>
      setAccountConnected(profile, true);

    const disconnectAccount = (profile: GoogleProfile) =>
      setAccountConnected(profile, false);

    const accountActionState = (profile: GoogleProfile): ButtonState =>
      updatingAccount.value === profile.kind.identity
        ? "loading"
        : updatingAccount.value !== null || isUpdating.value
          ? "disabled"
          : "default";

    const addAccount = async () => {
      notifyCheckBrowser(controller);
      openExternalUrl(
        await controller.auth.attachAccount(window.location.href),
        null,
        false
      );
    };

    const openUrl = (url: string) => openExternalUrl(url, controller);

    const refreshingStatus = ref<{ title: string; state: ButtonState }>({
      title: "Refresh",
      state: "default",
    });
    const refreshCalendars = async () => {
      refreshingStatus.value = { title: "Refreshing…", state: "loading" };
      try {
        const accounts = googleProfiles.value
          .filter(isFeatureActive)
          .map((profile) => profile.kind.identity);
        await Promise.all(
          accounts.map((account) => controller.calendar.refresh(account))
        );
      } finally {
        refreshingStatus.value = { title: "Refresh", state: "default" };
      }
    };

    return {
      config,
      removalHint,
      scopedProfiles,
      unscopedProfiles,
      isFeatureActive,
      isUpdating,
      enableFeature,
      updatingAccount,
      accountActionState,
      connectAccount,
      disconnectAccount,
      addAccount,
      openUrl,
      refreshCalendars,
      refreshingStatus,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

h3 {
  margin-top: 24px;
  margin-bottom: 8px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  margin-bottom: 8px;

  h3 {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.account {
  display: flex;
  align-items: center;
  gap: 8px;

  .account-icon {
    height: 20px;
    width: 20px;
  }
}

.entry.disconnected .account {
  .account-icon {
    opacity: 0.5;
  }

  label {
    @include themed() {
      color: t($grey-50);
    }
  }
}

.entry-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.removal-hint {
  display: inline-flex;
  align-items: center;
  cursor: default;

  :deep(svg) {
    width: 14px;
    height: 14px;
  }
}

.add-account {
  margin-top: 24px;
}
</style>
